import { createContext, MutableRefObject, useContext } from 'react';

import { OnGetContentRef } from 'modules/dragAndDropEditor/types';
import { HtmlEditor } from 'modules/graphql/types';
import noop, { NoOp } from 'utils/noop';

import {
  EditorView,
  MediumDataKey,
  SelectedMessageMedium,
  TemplateAndContentFormData,
  TemplateAndContentFormDataLocale,
  UpdateMediumFieldsType,
} from '../common/types';

export interface EditableFieldsContextState {
  contentEditor: HtmlEditor | undefined;
  contentEditorView: EditorView;
  getDragAndDropContentRef: MutableRefObject<OnGetContentRef | undefined>;
  dragAndDropInitTs: number; // primitive to trigger a re-init of the dnd editor (even if locale and init value do not change)
  dragAndDropInitValue: string;
  hasContent: boolean;
  initialTemplate: TemplateAndContentFormDataLocale;
  isDirty: boolean;
  isEditMode: boolean;
  liveTemplate: TemplateAndContentFormDataLocale;
  resetLiveTemplate: () => void;
  selectedMessageMedium: SelectedMessageMedium | null;
  setContentEditorView: (view: EditorView, callback?: NoOp) => void;
  setCurrentMediumField: (fieldName: MediumDataKey, value: any) => void;
  setCurrentMediumFields: (updatedFields: UpdateMediumFieldsType) => void;
  setCurrentTemplateField: (fieldName: keyof TemplateAndContentFormData, value: any) => void;
  setDragAndDropInitTs: (initTs?: number) => void;
  setDragAndDropInitValue: (initValue?: string) => void;
  setIsEditMode: (newMode: boolean) => void;
  setSuggestedContent?: (suggestedFields?: UpdateMediumFieldsType) => void;
  skipUnsavedChangesCheck: MutableRefObject<boolean>;
  suggestedContent?: UpdateMediumFieldsType;
}

export const INITIAL_EDITABLE_FIELDS_CONTEXT_STATE: EditableFieldsContextState = {
  contentEditor: undefined,
  contentEditorView: EditorView.HTML,
  getDragAndDropContentRef: { current: undefined },
  dragAndDropInitTs: 0,
  dragAndDropInitValue: '{}',
  hasContent: false,
  initialTemplate: {} as TemplateAndContentFormDataLocale,
  isDirty: false,
  isEditMode: false,
  liveTemplate: {} as TemplateAndContentFormDataLocale,
  resetLiveTemplate: noop,
  selectedMessageMedium: null,
  setContentEditorView: noop,
  setCurrentMediumField: noop,
  setCurrentMediumFields: noop,
  setCurrentTemplateField: noop,
  setDragAndDropInitTs: noop,
  setDragAndDropInitValue: noop,
  setIsEditMode: noop,
  setSuggestedContent: noop,
  skipUnsavedChangesCheck: { current: false },
  suggestedContent: {},
};

export const EditableFieldsContext = createContext<EditableFieldsContextState>(
  INITIAL_EDITABLE_FIELDS_CONTEXT_STATE,
);

export const useEditableFieldsContext = () => useContext(EditableFieldsContext);
